<template>

  <div class="viewport viewport-with-top-nav viewport-compact">

    <TopNav></TopNav>

    <div class="display-1 py-3">{{$t('LoanStepContactsPage.title')}}</div>

    <div v-if="processingApplication" class="pt-4 mt-2">
      <div class="card card-shadow card-rounded border-0 mb-4">
        <div class="card-body py-5 text-center">
          <spinner></spinner>
        </div>
      </div>
    </div>
    <div v-else>
      <div v-if="errors.length > 0" class="alert alert-danger">
        <div v-for="(error, index) in errors" :key="index">{{ error }}</div>
      </div>

      <div class="mb-4">
        <div class="d-flex justify-content-between align-items-center">
          <div class="small text-muted" v-html="$t('LoanStepContactsPage.alreadyHaveALoanWithUs')"></div>
          <div class="ml-3">
            <router-link :to="{name: 'login'}" class="btn btn-sm btn-secondary text-nowrap">{{$t('LoanStepContactsPage.logIn')}}</router-link>
          </div>
        </div>
      </div>

      <validation-observer ref="form">
        <div class="form-group">
          <validation-provider vid="borrower.firstName" rules="required" v-slot="{ errors }">
            <label for="firstName">{{$t('LoanStepContactsPage.firstName')}}</label>
            <input type="text" v-model.trim="application.borrower.firstName" id="firstName" class="form-control form-control-lg" :class="{'is-invalid': errors.length}" inputmode="text">
            <span class="invalid-feedback">{{ errors[0] }}</span>
          </validation-provider>
        </div>
        <div class="form-group">
          <validation-provider vid="borrower.lastName" rules="required" v-slot="{ errors }">
            <label for="lastName">{{$t('LoanStepContactsPage.lastName')}}</label>
            <input type="text" v-model.trim="application.borrower.lastName" id="lastName" class="form-control form-control-lg" :class="{'is-invalid': errors.length}" inputmode="text">
            <span class="invalid-feedback">{{ errors[0] }}</span>
          </validation-provider>
        </div>
        <div class="form-group ">
          <validation-provider vid="borrower.pin" ref="pin" rules="pin" v-slot="{ errors }">
            <label for="userPin">{{$t('LoanStepContactsPage.personalCode')}}</label>
            <input type="text" v-model.trim="application.borrower.pin" id="userPin"
                   placeholder="0123456789123" class="form-control form-control-lg" :class="{'is-invalid': errors.length}"
            >
            <span v-if="application.borrower.birthDate" class="text-muted">
              {{$t('LoanStepContactsPage.birthDate', {birthDate: $d(application.borrower.birthDate)})}}
            </span>
            <span class="invalid-feedback">{{ errors[0] }}</span>
          </validation-provider>
        </div>
        <div class="form-group">
          <validation-provider vid="borrower.phone" rules="required|numeric" v-slot="{ errors }">
            <label for="userPhone">{{$t('LoanStepContactsPage.phone')}}</label>
            <input type="text" v-model.trim="application.borrower.phone"
                   placeholder="0601234567" id="userPhone" class="form-control form-control-lg"
                   maxlength="10" pattern="06[0-7]\d{6,7}"
                   :class="{'is-invalid': errors.length}"
                   inputmode="tel">
            <span class="text-muted">{{$t('LoanStepContactsPage.phoneHint')}}</span>
            <span class="invalid-feedback">{{ errors[0] }}</span>
          </validation-provider>
        </div>
        <div class="form-group">
          <validation-provider vid="borrower.email" rules="required|email" v-slot="{ errors }" mode="eager">
            <label for="userEmail">{{$t('LoanStepContactsPage.email')}}</label>
            <input type="text" v-model.trim="application.borrower.email" id="userEmail"
                   class="form-control form-control-lg" :class="{'is-invalid': errors.length}" inputmode="email"
                   @keyup="validate">
            <span class="invalid-feedback">{{ errors[0] }}</span>
          </validation-provider>
        </div>

        <div>
          <button id="button-submit-contacts" class="btn btn-lg btn-primary btn-block" @click="submitContactStep()">
            {{$t('LoanStepContactsPage.continueBtn')}}
          </button>
        </div>
      </validation-observer>
    </div>
    <div class="flex-grow-1"></div>

  </div>
</template>

<script>
import TopNav from '@shared/components/TopNav'
import store from '@shared/store'
import _debounce from 'lodash/debounce'
import ValidationErrors from '@shared/mixins/ValidationErrors'
import applicationLanguage from '@shared/module/applicationLanguage'

export default {
    name: 'LoanStepContactsPage',
    components: { TopNav },
    mixins: [ValidationErrors],
    data () {
      return {
        application: store.getApplication(),
        processingApplication: false,
        errors: []
      }
    },
    created() {
      this.onPinChangedDebounced = _debounce(this.calculateBirthDate, 600)
    },
    watch: {
      'application.borrower.pin': function(newValue, oldValue) {
        this.$set(this.application.borrower, 'pin', newValue.toUpperCase())
        this.onPinChangedDebounced(newValue, oldValue)
      }
    },
    methods: {
      validate() {
        return this.$refs.form.validate()
      },
      submitContactStep() {
        this.$refs.form.validate()
            .then(this.afterFormValidation)
            .then(this.proceed)
            .catch(this.onValidationServerSideError)
      },

      afterFormValidation(success) {
        if (success) {
          this.processingApplication = true
          return this.validateServerSide()
        } else {
          return Promise.reject(new Error('Customer\'s data contain error'))
        }
      },

      validateServerSide() {
        return this.$http.post('/api/applications/validate', { ...this.application, locale: applicationLanguage() })
      },

      onValidationServerSideError(error) {
        this.processingApplication = false
        this.handleValidationErrors(error.response)
      },

      proceed() {
        store.setApplication(this.application)
        const customerPin = this.application.borrower.pin
        this.$http.get(`/api/customers/${customerPin}`)
            .then(response => {
              const customer = response.data
              if (customer.type === 'NEW') {
                this.$router.push({ name: 'new-customer-agreement' })
                return
              }
              if (customer.hasOffer) {
                this.$router.push({ name: 'returning-customer-otp', query: { withUsedOffer: customer.hasUsedOffers } })
              } else {
                this.$router.push({ name: 'without-offer-agreement' })
              }
            })
      },

      setBirthDate(date) {
        this.$set(this.application.borrower, 'birthDate', date)
      },

      calculateBirthDate(pin) {
        this.setBirthDate(null)
        if (pin.trim().length === 0) {
          return Promise.resolve()
        }
        return this.$http.get(`/api/validation/extract/birthdate/${pin}`)
            .then(response => {
              this.setBirthDate(response.data.birthDate)
            })
      }
    }
  }
</script>
